/* ============================================================
   GlobalLoader — the brand glyph, drawing itself in and out on an infinite
   alternating loop, over a blurred app while a screen is loading.

   It only appears at two moments:
     · the first load of the app, and
     · moving to a different screen (a new route path).
   Each opens a "loading window" that lasts until that screen's first data has
   arrived. Once the screen has settled, the window closes and later API calls
   on the same screen — searches, opening a record, saves, polling — never blur
   it again; their buttons and inline spinners show progress instead. The next
   route change opens a new window. (Before this, every first fetch and every
   save blurred the whole app, which made it flash constantly.)

   Inside a window it counts queries fetching with nothing on screen yet.
   Queries no component is reading (prefetches), queries that keep previous
   results on screen (placeholderData), polling queries, and queries that
   already failed are skipped, and anything can opt out with
   `meta: { globalLoader: false }`.

   Timing: shown only after SHOW_DELAY_MS so a fast response never flashes it,
   then kept for at least MIN_VISIBLE_MS so it does not flicker off at once.
   A window also closes after MAX_WINDOW_MS so a slow screen cannot hold it.
   ============================================================ */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useIsFetching } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'

import { BRAND_GLYPH_PATH } from '../components/BrandMark'

const SHOW_DELAY_MS = 300
const MIN_VISIBLE_MS = 500
// How long after a route change its first queries may still start (lazy
// screens mount, then fetch) before the window gives up waiting for them.
const START_GRACE_MS = 600
const MAX_WINDOW_MS = 15000

/* ---- route window: opened on first load and on every path change ---- */
let windowId = 1 // the first app load is window 1
const listeners = new Set()
const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn) }
const getWindow = () => windowId
function openWindow() {
  windowId += 1
  listeners.forEach((fn) => fn())
}

/** Mount once inside the router: a new path opens a new loading window. */
export function RouteLoaderWindow() {
  const { pathname } = useLocation()
  const first = useRef(true)
  useEffect(() => {
    if (first.current) { first.current = false; return }
    openWindow()
  }, [pathname])
  return null
}

const optedOut = (meta) => meta?.globalLoader === false

function blocksScreen(query) {
  if (optedOut(query.meta)) return false
  // Nothing on screen is reading it: a prefetch (e.g. Inbox hover-prefetch,
  // which fires as the pointer rests on rows while scrolling) — never waited on.
  if (query.getObserversCount() === 0) return false
  if (query.state.data !== undefined) return false
  // Already failed: a re-poll or retry of it must not strobe the screen.
  if (query.state.status === 'error') return false
  return !query.observers.some((o) =>
    // Something is on screen already (placeholder / previous results)…
    o.options.placeholderData !== undefined
    // …or it is a polling widget (bell, badges, run status) — never waited on.
    || Boolean(o.options.refetchInterval))
}

/** True while the current route window is still loading its first data. */
export function useGlobalBusy() {
  const fetching = useIsFetching({ predicate: blocksScreen }) > 0
  const win = useSyncExternalStore(subscribe, getWindow)
  const [closed, setClosed] = useState(0) // id of the last window that settled
  const sawWork = useRef(false)

  // A new window: forget the previous one's progress.
  useEffect(() => { sawWork.current = false }, [win])

  useEffect(() => {
    if (closed === win) return undefined
    if (fetching) {
      sawWork.current = true
      const cap = setTimeout(() => setClosed(win), MAX_WINDOW_MS)
      return () => clearTimeout(cap)
    }
    // Settled after loading — or nothing started within the grace period.
    const t = setTimeout(() => setClosed(win), sawWork.current ? 0 : START_GRACE_MS)
    return () => clearTimeout(t)
  }, [fetching, win, closed])

  return closed !== win && fetching
}

export default function GlobalLoader() {
  const busy = useGlobalBusy()
  const [visible, setVisible] = useState(false)
  const shownAt = useRef(0)

  useEffect(() => {
    if (busy) {
      if (visible) return undefined
      const t = setTimeout(() => {
        shownAt.current = Date.now()
        setVisible(true)
      }, SHOW_DELAY_MS)
      return () => clearTimeout(t)
    }
    if (!visible) return undefined
    const left = Math.max(0, MIN_VISIBLE_MS - (Date.now() - shownAt.current))
    const t = setTimeout(() => setVisible(false), left)
    return () => clearTimeout(t)
  }, [busy, visible])

  if (!visible) return null
  return (
    <div className="app-loader" role="status" aria-live="polite" aria-busy="true">
      <div className="app-loader-tile">
        <svg className="app-loader-glyph" viewBox="0 0 100 64.46" aria-hidden="true">
          <path d={BRAND_GLYPH_PATH} pathLength="1" />
        </svg>
      </div>
      <span className="app-loader-sr">Loading…</span>
    </div>
  )
}
